/**
 * EditableText / EditableTitle — inline text for presentation sections.
 *
 * Present mode: renders Markdown for bodies, plain text for titles (nothing when
 * empty). Edit mode (team): an UNCONTROLLED contentEditable that seeds its text
 * from `value` via a ref (so re-renders never clobber in-progress typing) and
 * commits on blur via onCommit. One component, branching on editable.
 */

import { useEffect, useRef } from 'react'
import { useEditable } from './PresentationContext'
import Markdown from '../components/Markdown'

export function EditableTitle({ value, onCommit, className = 'pr-section-heading', placeholder = 'Title' }) {
  return <EditableText value={value} onCommit={onCommit} as="div" className={className} placeholder={placeholder} plain />
}

export default function EditableText({
  value, onCommit, className = 'pr-body', as: Tag = 'div', placeholder = '', plain = false,
}) {
  const editable = useEditable()
  const ref = useRef(null)

  // Seed the editable element from `value` only when it's not focused, so the
  // user's caret/typing is never reset by a parent re-render.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (document.activeElement !== el && el.innerText !== (value || '')) {
      el.innerText = value || ''
    }
  }, [value, editable])

  if (!editable) {
    if (!value) return null
    return plain
      ? <Tag className={className}>{value}</Tag>
      : <div className={className}><Markdown text={value} /></div>
  }

  function commit() {
    const next = ref.current ? ref.current.innerText.trim() : ''
    if (onCommit && next !== (value || '')) onCommit(next)
  }

  return (
    <Tag
      ref={ref}
      className={className + ' pr-editable'}
      contentEditable
      suppressContentEditableWarning
      data-placeholder={placeholder}
      onBlur={commit}
      style={{ outline: 'none', minHeight: '1em' }}
    />
  )
}
